import { Dispatch, SetStateAction } from "react";
import { CollisionModeEnum } from "./Data";

import { fixCollisions } from "./collisionHelper";

export interface MoveActions {
	moveElement: (axis: AxisEnum, value: number) => void;
	rotateElement: (axis: RotationEnum, value: 1 | -1) => void;
}

export function moveActionsHelper(
	setElementEditing: Dispatch<SetStateAction<ContainerElementRef | undefined>>,
	elementsMap: FunctionalMap<string, ElementDefinitionType>,
	containersMap: FunctionalMap<string, ContainerType>,
	elementsPositionMap: FunctionalMap<string, ContainerElementRef>,
	collisionModeRef: React.MutableRefObject<CollisionModeEnum>
): MoveActions {
	const updateElement: (
		axis: AxisEnum,
		value: number,
		positionUpdate?: (position: ElementPositionType) => void
	) => void = (axis, value, positionUpdate) => {
		setElementEditing(elementEditing => {
			if (elementEditing) {
				const { elementId, position } = elementEditing;
				const containerRef = elementsPositionMap.get(elementId);
				const container = containerRef && containersMap.get(containerRef.containerId);
				if (!container) {
					// An element can only be updated in the view (position, rotation)
					// The other properties (id, width, height, color, ...) cannot be updated... for now...
					return undefined;
				}
				const element = elementsMap.get(elementId);
				if (!element) {
					console.error(`Unknown element '${elementId}'`);
					return elementEditing;
				}

				const newPosition = { ...position };
				if (newPosition.position) {
					// Clone position as it could be mutated on Y axis if collisions are detected...
					newPosition.position = [...newPosition.position];
				} else {
					newPosition.position = [0, 0, 0];
				}
				const axisMapper = { x: 0, y: 1, z: 2 };
				newPosition.position[axisMapper[axis]] =
					Math.round((newPosition.position[axisMapper[axis]] + value) * 100) / 100;

				positionUpdate && positionUpdate(newPosition);

				const newRef = {
					elementId,
					containerId: container.id,
					position: newPosition
				};

				const collisionMode = collisionModeRef.current;
				if (collisionMode !== "IGNORE") {
					if (newRef.position.position) {
						// Clone position as it could be mutated on Y axis if collisions are detected...
						newRef.position.position = [...newRef.position.position];
					}

					const collisionAxis = collisionMode === "MOVE_UP" ? "y" : axis;
					const moveUp =
						collisionMode === "MOVE_UP"
							? axis !== "y" || value >= 0
							: collisionMode === "MOVE_PAST"
							? value >= 0
							: value <= 0;

					fixCollisions(
						container.content.filter(eltId => eltId !== elementId),
						elementsMap,
						elementsPositionMap,
						newRef,
						collisionAxis,
						moveUp
					);
				}
				elementsPositionMap.set(newRef.elementId, newRef);
				return newRef;
			}
			return elementEditing;
		});
	};

	return {
		moveElement: (axis, value) => updateElement(axis, value),
		rotateElement: (axis, quadrantValue) =>
			updateElement("y", 0, position => {
				position[axis] = (((position[axis] || 0) + quadrantValue + 4) % 4) as RotationType;
			})
	};
}
